import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import ChangePasswordModal from './ChangePasswordModal';

interface AdminLoginProps {
  onLogin: () => void;
}

export default function AdminLogin({ onLogin }: AdminLoginProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error: supabaseError } = await supabase
        .from('dj_profile')
        .select('admin_password')
        .maybeSingle();

      if (supabaseError) {
        throw supabaseError;
      }

      const storedPassword = data?.admin_password ?? 'dj-admin-2025';

      if (password === storedPassword) {
        sessionStorage.setItem('dj_admin_auth', 'true');
        onLogin();
      } else {
        setError('Contraseña incorrecta. Inténtalo de nuevo.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Title */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full border-2 border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.4)]">
            <i className="ri-shield-keyhole-line text-cyan-400 text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-widest uppercase">
            Panel <span className="text-cyan-400">Admin</span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">Acceso restringido al gestor de eventos</p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-gray-900 border border-gray-800 rounded-xl p-8 shadow-2xl"
        >
          <div className="mb-6">
            <label className="block text-gray-400 text-xs uppercase tracking-widest mb-2">
              Contraseña
            </label>
            <div className="relative">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Introduce tu contraseña"
                className="w-full bg-black border border-gray-700 text-white text-sm rounded-lg px-4 py-3 pr-10 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(34,211,238,0.2)] transition-all placeholder-gray-600"
                required
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-lock-line text-gray-500 text-base"></i>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg px-4 py-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-400 text-sm"></i>
              </div>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-cyan-400 hover:bg-cyan-300 text-black font-bold text-sm uppercase tracking-widest py-3 rounded-lg transition-all duration-200 shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.5)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                Verificando...
              </span>
            ) : (
              'Entrar al panel'
            )}
          </button>

          {/* Change password link */}
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center justify-center gap-1.5 mx-auto text-gray-500 hover:text-cyan-400 text-xs transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-lock-password-line text-xs"></i>
              </div>
              Cambiar contraseña
            </button>
          </div>
        </form>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal
          profileId={null}
          onClose={() => setShowPasswordModal(false)}
        />
      )}
    </div>
  );
}
